import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Card,
  CardContent,
  CardActionArea,
  Chip,
  Alert
} from '@mui/material';
import {
  EditNote as ManualIcon,
  Upload as UploadIcon,
  Sync as SyncIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

/**
 * Fidelity Connection Modal
 * Shown when user selects Fidelity in Plaid Link
 * Offers three connection options: Manual, CSV Import, Auto-Sync (Coming Soon)
 */
const FidelityConnectionModal = ({ open, onClose }) => {
  const navigate = useNavigate();

  const handleManualEntry = () => {
    onClose();
    navigate('/investments/manual-entry');
  };

  const handleCSVImport = () => {
    onClose();
    navigate('/investments/csv-import');
  };

  const connectionOptions = [
    {
      id: 'manual',
      title: 'Manual Entry',
      description: 'Enter your investment holdings manually. We\'ll automatically update prices daily!',
      icon: <ManualIcon sx={{ fontSize: 40 }} />,
      color: 'primary',
      available: true,
      onClick: handleManualEntry
    },
    {
      id: 'csv',
      title: 'Import CSV',
      description: 'Upload a CSV file from Fidelity (or other brokerages) to import your holdings.',
      icon: <UploadIcon sx={{ fontSize: 40 }} />,
      color: 'secondary',
      available: true,
      onClick: handleCSVImport
    },
    {
      id: 'auto',
      title: 'Auto-Sync',
      description: 'Automatically sync your Fidelity account daily.',
      icon: <SyncIcon sx={{ fontSize: 40 }} />,
      color: 'success',
      available: false,
      comingSoon: true,
      onClick: null
    }
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: 24
        }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h5" component="div" fontWeight="bold">
          Connect Fidelity Account
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Choose how you'd like to add your Fidelity investment accounts
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            Fidelity requires a different connection method than standard bank accounts. 
            Choose the option that works best for you below.
          </Typography>
        </Alert>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {connectionOptions.map((option) => (
            <Card
              key={option.id}
              variant="outlined"
              sx={{
                position: 'relative',
                opacity: option.available ? 1 : 0.6,
                '&:hover': option.available
                  ? {
                      boxShadow: 4,
                      transform: 'translateY(-2px)',
                      transition: 'all 0.2s ease-in-out'
                    }
                  : {}
              }}
            >
              <CardActionArea
                onClick={option.onClick}
                disabled={!option.available}
                sx={{ height: '100%' }}
              >
                <CardContent sx={{ display: 'flex', alignItems: 'center', p: 3 }}>
                  {/* Icon */}
                  <Box
                    sx={{
                      mr: 3,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 60,
                      height: 60,
                      borderRadius: 2,
                      bgcolor: option.available
                        ? `${option.color}.50`
                        : 'grey.200',
                      color: option.available ? `${option.color}.main` : 'grey.500'
                    }}
                  >
                    {option.icon}
                  </Box>

                  {/* Content */}
                  <Box sx={{ flexGrow: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                      <Typography variant="h6" component="div" fontWeight="600">
                        {option.title}
                      </Typography>
                      {option.comingSoon && (
                        <Chip
                          label="Coming Soon"
                          size="small"
                          color="warning"
                          sx={{ ml: 1.5, fontWeight: 600 }}
                        />
                      )}
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      {option.description}
                    </Typography>
                  </Box>
                </CardContent>
              </CardActionArea>
            </Card>
          ))}
        </Box>

        {/* Additional Info */}
        <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary">
            <strong>💡 Pro Tip:</strong> Even with manual entry or CSV import, 
            we automatically update your stock prices daily using real-time market data!
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={onClose} variant="outlined" color="inherit">
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FidelityConnectionModal;
